import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, ChevronDown, ChevronUp, ThumbsUp, ThumbsDown } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { PriceTracker } from '@/components/PriceTracker';
import { motion, AnimatePresence } from 'framer-motion';

interface ProductDetailsProps {
  productId: string;
  expanded: boolean;
  onToggle: () => void;
  onPriceUpdate?: () => void;
}

interface Note {
  id: string;
  content: string;
  created_at: string;
}

interface Argument {
  id: string;
  content: string;
  type: 'pro' | 'contra';
  created_at: string;
}

export const ProductDetails = ({ productId, expanded, onToggle, onPriceUpdate }: ProductDetailsProps) => {
  const { user } = useAuth();
  const [notes, setNotes] = useState<Note[]>([]);
  const [proContraList, setProContraList] = useState<Argument[]>([]);
  const [newNote, setNewNote] = useState('');
  const [newPro, setNewPro] = useState('');
  const [newContra, setNewContra] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (expanded) {
      fetchData();
    }
  }, [expanded, productId]);

  const fetchData = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data: notesData, error: notesError } = await supabase
        .from('product_notes')
        .select('*')
        .eq('product_id', productId)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (notesError) throw notesError;
      setNotes(notesData || []);

      const { data: argumentsData, error: argumentsError } = await supabase
        .from('product_arguments')
        .select('*')
        .eq('product_id', productId)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (argumentsError) throw argumentsError;
      setProContraList(argumentsData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Fehler beim Laden der Details');
    } finally {
      setLoading(false);
    }
  };

  const addNote = async () => {
    if (!user || !newNote.trim()) return;

    try {
      const { error } = await supabase
        .from('product_notes')
        .insert([
          {
            product_id: productId,
            user_id: user.id,
            content: newNote.trim()
          }
        ]);

      if (error) throw error;

      setNewNote('');
      fetchData();
      toast.success('Notiz hinzugefügt');
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Fehler beim Hinzufügen der Notiz');
    }
  };

  const addArgument = async (type: 'pro' | 'contra') => {
    if (!user) return;
    const content = type === 'pro' ? newPro : newContra;
    if (!content.trim()) return;

    try {
      const { error } = await supabase
        .from('product_arguments')
        .insert([
          {
            product_id: productId,
            user_id: user.id,
            content: content.trim(),
            type
          }
        ]);

      if (error) throw error;

      type === 'pro' ? setNewPro('') : setNewContra('');
      fetchData();
      toast.success(`${type === 'pro' ? 'Pro' : 'Contra'}-Argument hinzugefügt`);
    } catch (error) {
      console.error('Error adding argument:', error);
      toast.error('Fehler beim Hinzufügen des Arguments');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>, action: () => void) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      action();
    }
  };

  const handleButtonClick = (e: React.MouseEvent, action: () => void) => {
    e.preventDefault();
    e.stopPropagation();
    action();
  };

  if (!expanded) {
    return null;
  }

  return (
    <AnimatePresence>
      {expanded && (
        <motion.div
          initial={{ height: 0, opacity: 0, y: -20 }}
          animate={{ height: "auto", opacity: 1, y: 0 }}
          exit={{ height: 0, opacity: 0, y: -20 }}
          transition={{ 
            duration: 0.4, 
            ease: [0.4, 0.0, 0.2, 1],
            opacity: { duration: 0.3 },
            y: { duration: 0.4 }
          }}
          className="border-t border-gray-100 overflow-hidden mt-4"
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggle}
            className="w-full flex items-center justify-between py-3 hover:bg-gray-50 text-gray-600"
          >
            <span className="text-sm">Details ausblenden</span>
            <ChevronUp className="w-4 h-4" />
          </Button>

          <div className="p-6 space-y-6 bg-gray-50/50">
            <PriceTracker 
              productId={productId} 
              key={productId}
              onPriceUpdate={onPriceUpdate}
            />

            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <h3 className="text-sm font-semibold mb-3 text-gray-900">Notizen</h3>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    onKeyPress={(e) => handleKeyPress(e, addNote)}
                    placeholder="Neue Notiz hinzufügen..."
                    className="text-sm border-gray-300 focus:border-emerald-500"
                  />
                  <Button onClick={(e) => handleButtonClick(e, addNote)} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white px-3">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {notes.map((note) => (
                  <div key={note.id} className="bg-gray-50 p-3 rounded-lg text-sm border border-gray-200">
                    <p className="text-gray-800">{note.content}</p>
                    <p className="text-xs text-gray-500 mt-2">
                      {new Date(note.created_at).toLocaleDateString('de-DE')}
                    </p>
                  </div>
                ))}
                {notes.length === 0 && (
                  <p className="text-sm text-gray-500 italic text-center py-4">
                    Noch keine Notizen vorhanden
                  </p>
                )}
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <h3 className="text-sm font-semibold mb-3 text-gray-900">Pro & Contra</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex gap-2">
                    <Input
                      value={newPro}
                      onChange={(e) => setNewPro(e.target.value)}
                      onKeyPress={(e) => handleKeyPress(e, () => addArgument('pro'))}
                      placeholder="Pro-Argument..."
                      className="text-sm border-gray-300 focus:border-green-500"
                    />
                    <Button 
                      onClick={(e) => handleButtonClick(e, () => addArgument('pro'))} 
                      size="sm" 
                      className="bg-green-600 hover:bg-green-700 text-white px-3 transition-all duration-200"
                    >
                      <ThumbsUp className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={newContra}
                      onChange={(e) => setNewContra(e.target.value)}
                      onKeyPress={(e) => handleKeyPress(e, () => addArgument('contra'))}
                      placeholder="Contra-Argument..."
                      className="text-sm border-gray-300 focus:border-red-500"
                    />
                    <Button 
                      onClick={(e) => handleButtonClick(e, () => addArgument('contra'))} 
                      size="sm" 
                      className="bg-red-600 hover:bg-red-700 text-white px-3 transition-all duration-200"
                    >
                      <ThumbsDown className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <h4 className="text-xs font-medium text-green-700 uppercase tracking-wide">Pro</h4>
                    {proContraList
                      .filter(arg => arg.type === 'pro')
                      .map((arg) => (
                        <div key={arg.id} className="bg-green-50 p-3 rounded-lg text-sm flex items-start gap-2 border border-green-200">
                          <ThumbsUp className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                          <p className="text-green-800">{arg.content}</p>
                        </div>
                      ))}
                    {proContraList.filter(arg => arg.type === 'pro').length === 0 && (
                      <p className="text-xs text-gray-500 italic text-center py-3">
                        Keine Pro-Argumente
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <h4 className="text-xs font-medium text-red-700 uppercase tracking-wide">Contra</h4>
                    {proContraList
                      .filter(arg => arg.type === 'contra')
                      .map((arg) => (
                        <div key={arg.id} className="bg-red-50 p-3 rounded-lg text-sm flex items-start gap-2 border border-red-200">
                          <ThumbsDown className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                          <p className="text-red-800">{arg.content}</p>
                        </div>
                      ))}
                    {proContraList.filter(arg => arg.type === 'contra').length === 0 && (
                      <p className="text-xs text-gray-500 italic text-center py-3">
                        Keine Contra-Argumente
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}; 